import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid';
import HeightVolumeChart from './HeightVolumeChart';

const generateUnifiedDataPointId = () => `udp_${uuidv4()}`;
const VALIDATION_KEY_NAMESPACE = uuidv5.URL;
const buildValidationMessageKey = (message, index) => uuidv5(`${message}-${index}`, VALIDATION_KEY_NAMESPACE);

/**
 * Creates unified data from multiple page tables for gauged containers
 */
export const createUnifiedData = (pageTables) => {
  if (!pageTables || pageTables.length === 0) {
    return {
      heights: [],
      volumes: [],
      dataPointIds: [],
      pageNumbers: [],
      isValid: false,
      totalPoints: 0,
    };
  }

  // For gauged containers, we combine all data points preserving original order
  const heights = [];
  const volumes = [];
  const dataPointIds = [];
  const pageNumbers = [];

  pageTables.forEach((table) => {
    // Preserve the order within each table as edited/inserted by the user
    table.heights.forEach((h, i) => {
      heights.push(h);
      volumes.push(table.volumes[i]);
      pageNumbers.push(table.pageNumber);
      const existingId = table.dataPointIds?.[i];
      dataPointIds.push(existingId || generateUnifiedDataPointId());
    });
  });

  return {
    heights,
    volumes,
    dataPointIds,
    pageNumbers,
    isValid: heights.length > 0 && volumes.length > 0,
    totalPoints: heights.length,
  };
};

/**
 * Validates unified data for gauged containers
 */
export const validateUnifiedData = (unifiedData) => {
  const { heights, volumes } = unifiedData;
  const errors = [];
  const warnings = [];

  if (heights.length === 0 || volumes.length === 0) {
    errors.push('No data points available');
    return { errors, warnings, isValid: false };
  }

  if (heights.length !== volumes.length) {
    errors.push('Height and volume arrays have different lengths');
    return { errors, warnings, isValid: false };
  }

  // Check for proper height progression
  for (let i = 0; i < heights.length - 1; i += 1) {
    if (heights[i] >= heights[i + 1]) {
      errors.push(`Height progression error: ${heights[i]} >= ${heights[i + 1]}`);
    }
  }

  // Check for reasonable volume progression
  for (let i = 0; i < volumes.length - 1; i += 1) {
    if (volumes[i] < 0 || volumes[i + 1] < 0) {
      errors.push('Negative volume values found');
    }

    // Volume should generally increase with height
    if (heights[i] < heights[i + 1] && volumes[i] > volumes[i + 1]) {
      warnings.push(`Volume decreases while height increases at height ${heights[i]}`);
    }
  }

  // Check for gaps in height progression
  const heightSteps = [];
  for (let i = 0; i < heights.length - 1; i += 1) {
    heightSteps.push(heights[i + 1] - heights[i]);
  }

  if (heightSteps.length > 0) {
    const avgStep = heightSteps.reduce((sum, step) => sum + step, 0) / heightSteps.length;
    const largeGaps = heightSteps.filter((step) => step > avgStep * 2);

    if (largeGaps.length > 0) {
      warnings.push(`${largeGaps.length} large gaps detected in height progression`);
    }
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
  };
};

/**
 * Exports unified data to CSV format
 */
export const exportToCsv = (unifiedData, metadata = {}) => {
  const { heights, volumes } = unifiedData;

  const csvRows = [];

  // Add metadata header
  if (metadata.certificateId) {
    csvRows.push(`Certificate ID,${metadata.certificateId}`);
  }
  if (metadata.containerId) {
    csvRows.push(`Container ID,${metadata.containerId}`);
  }
  if (metadata.totalVolume) {
    csvRows.push(`Total Volume,${metadata.totalVolume}`);
  }
  csvRows.push(''); // Empty row

  // Add data header
  csvRows.push('Height (m),Volume (m³)');

  // Add data rows
  heights.forEach((height, index) => {
    csvRows.push(`${height.toFixed(3)},${volumes[index].toFixed(3)}`);
  });

  return csvRows.join('\n');
};

/**
 * Unified data view for gauged containers
 */
export default function UnifiedDataView({
  pageTables,
  metadata,
  onExport,
  collapsible = false,
}) {
  const [unifiedData, setUnifiedData] = useState({
    heights: [],
    volumes: [],
    dataPointIds: [],
    pageNumbers: [],
    isValid: false,
    totalPoints: 0,
  });
  const [validation, setValidation] = useState({ errors: [], warnings: [], isValid: true });
  const [expanded, setExpanded] = useState(true);

  // Create unified data when page tables change
  useEffect(() => {
    const unified = createUnifiedData(pageTables);
    setUnifiedData(unified);

    const validationResult = validateUnifiedData(unified);
    setValidation(validationResult);
  }, [pageTables]);

  const handleExport = () => {
    const csvData = exportToCsv(unifiedData, metadata);
    onExport?.(csvData);
  };

  const content = (
    <Box>
      {!unifiedData.isValid && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Impossible de créer la vue unifiée. Veuillez vérifier les données des tableaux individuels.
        </Alert>
      )}

      {unifiedData.isValid && (
        <>
          {/* Summary Information */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">
                  Résumé des Données
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${unifiedData.totalPoints} points de données`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    icon={validation.isValid ? <CheckCircleIcon /> : <WarningIcon />}
                    label={validation.isValid ? 'Valide' : 'Problèmes Détectés'}
                    color={validation.isValid ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    size="small"
                  >
                    Exporter CSV
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Validation Messages */}
          {validation.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Erreurs de Validation :</Typography>
              {validation.errors.map((error, index) => (
                <Typography key={buildValidationMessageKey(error, index)} variant="body2">
                  •
                  {' '}
                  {error}
                </Typography>
              ))}
            </Alert>
          )}

          {validation.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Avertissements de Validation :</Typography>
              {validation.warnings.map((warning, index) => (
                <Typography key={buildValidationMessageKey(warning, index)} variant="body2">
                  •
                  {' '}
                  {warning}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Chart - Full Width */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Courbe hauteur-volume consolidée" />
            <CardContent>
              <HeightVolumeChart
                heights={unifiedData.heights}
                volumes={unifiedData.volumes}
                pointPageNumbers={unifiedData.pageNumbers}
                compartmentId="unified"
                title="Relation Complète Hauteur-Volume"
              />
            </CardContent>
          </Card>

          {/* Data Table - Full Width */}
          <Card>
            <CardHeader title="Tableau de Données Complet" />
            <CardContent>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Index</TableCell>
                      <TableCell>Hauteur (m)</TableCell>
                      <TableCell>Volume (m³)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unifiedData.heights.map((height, index) => {
                      const fallbackKey = `data-row-${height}-${unifiedData.volumes[index]}-${index}`;
                      const rowKey = unifiedData.dataPointIds?.[index] || fallbackKey;
                      return (
                        <TableRow key={rowKey}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{height.toFixed(3)}</TableCell>
                          <TableCell>{unifiedData.volumes[index].toFixed(3)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Data Statistics */}
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Statistiques des Données
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Hauteur Min
                </Typography>
                <Typography variant="h6">
                  {Math.min(...unifiedData.heights).toFixed(3)}
                  {' '}
                  m
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Hauteur Max
                </Typography>
                <Typography variant="h6">
                  {Math.max(...unifiedData.heights).toFixed(3)}
                  {' '}
                  m
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Volume Min
                </Typography>
                <Typography variant="h6">
                  {Math.min(...unifiedData.volumes).toFixed(3)}
                  {' '}
                  m³
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Volume Max
                </Typography>
                <Typography variant="h6">
                  {Math.max(...unifiedData.volumes).toFixed(3)}
                  {' '}
                  m³
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );

  if (!collapsible) {
    return content;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title="Vue Unifiée des Données"
        action={(
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-label="expand"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {content}
        </CardContent>
      </Collapse>
    </Card>
  );
}
